import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Unsaved Changes Dialog component
 * Warns users when navigating away with unsaved changes
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when dialog open state changes
 * @param {Array<string>} props.unsavedViewNames - Array of view names with unsaved changes
 * @param {Function} props.onSaveAll - Callback when "Save All" is clicked
 * @param {Function} props.onDiscardAll - Callback when "Discard All" is clicked
 * @param {Function} props.onCancel - Callback when "Cancel" is clicked
 * @param {boolean} props.isSaving - Whether save operations are in progress
 * @returns {JSX.Element} Unsaved Changes Dialog
 */
export default function UnsavedChangesDialog({
  open,
  onOpenChange,
  unsavedViewNames = [],
  onSaveAll,
  onDiscardAll,
  onCancel,
  isSaving = false,
}) {
  const handleSaveAll = async () => {
    if (onSaveAll) {
      await onSaveAll();
      // Parent handles closing the dialog after successful save
    } else {
      onOpenChange(false);
    }
  };

  const handleDiscardAll = () => {
    if (onDiscardAll) {
      onDiscardAll();
      // Parent handles closing the dialog after discard
    } else {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-lg font-semibold text-foreground'>
            Unsaved Changes
          </DialogTitle>
          <DialogDescription className='text-sm text-muted-foreground mt-2'>
            You have unsaved changes for the following views:
          </DialogDescription>
        </DialogHeader>
        <div className='py-4'>
          <ul className='list-disc list-inside space-y-1 text-sm text-foreground'>
            {unsavedViewNames.map((viewName, index) => (
              <li key={index} className='break-words'>
                {viewName}
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter className='flex flex-row justify-end gap-2'>
          <Button
            onClick={handleSaveAll}
            disabled={isSaving}
            className='font-light text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'>
            {isSaving ? "Saving..." : "Save All"}
          </Button>
          <Button
            variant='outline'
            onClick={handleDiscardAll}
            disabled={isSaving}
            className='font-light text-sm border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed'>
            Discard All
          </Button>
          <Button
            variant='outline'
            onClick={handleCancel}
            disabled={isSaving}
            className='font-light text-sm border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
